//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { MediaFilterParamKey } from '@apis/content-library/contentLibraryApiModel';
import Text from '@components/atoms/text/Text';
import { SelectedOption } from '@components/media-filters/SelectedOption';
import {
  FilterOption,
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';

export interface SelectedOptionsProps<T extends FilterValue> {
  isDisabled?: boolean;
  onDeselect: (
    paramKey: MediaFilterParamKey,
    selected: FilterOption<T>,
    subsection?: MediaFilterParamKey,
  ) => void;
  section: FilterSection<T>;
  subsection?: FilterSection<T>;
  title: string;
}

export const SelectedOptions = <T extends FilterValue>({
  isDisabled,
  onDeselect,
  section,
  subsection,
  title,
}: SelectedOptionsProps<T>) => (
  <SectionBox>
    <Text mixin={Mixins.Text.Metatag}>{title}</Text>
    {section.options.map((option) => (
      <SelectedOption
        key={option.label}
        isDisabled={isDisabled}
        onDeselect={() => {
          onDeselect(section.paramKey, option, subsection?.paramKey);
        }}
        option={option}
      />
    ))}
    {isDefined(subsection) && (
      <SubsectionBox>
        {subsection.options.map((option) => (
          <SelectedOption
            key={option.label}
            isDisabled={isDisabled}
            onDeselect={() => onDeselect(subsection.paramKey, option)}
            option={option}
          />
        ))}
      </SubsectionBox>
    )}
  </SectionBox>
);

const SectionBox = styled.section(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[1]};
  `,
);

const SubsectionBox = styled.section(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[1]};
    padding-left: ${theme.spacings[4]};
  `,
);
